import React, { FC, ReactNode, useContext } from 'react';
import { Checkbox, Tooltip } from '@clearcover/atlas';
import { Row, Column, FormInput } from 'Shared/components';
import styles from './styles.module.css';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import {
  DriverInfoOptionsType,
  DriverOptionQuestionsConfigType,
  ExcludedDriverOptionQuestionsConfigType,
} from '.';
import { monthDateYearNormalizer } from 'Shared/utils';
import { PolicyContext } from 'Policies/utils';
import { CoveredPartyPayloadType } from '@clearcover/core-app-types';

import {
  editDriverDetailsPermissionsPolicy,
  editDriverStatusPolicy,
  excludeDriverPermissionsPolicy,
} from 'Admin/authPolicies';
import { env } from 'Shared/constants/env';
import { useAuth0 } from 'Auth';

type DriverInfoProps = {
  options: DriverInfoOptionsType;
  driverOptionsQuestionsConfig: DriverOptionQuestionsConfigType;
  excludedDriverOptionsQuestionsConfig: ExcludedDriverOptionQuestionsConfigType;
};

export const DriverInfo: FC<DriverInfoProps> = ({
  options,
  driverOptionsQuestionsConfig,
  excludedDriverOptionsQuestionsConfig,
}) => {
  const { details, resourceEffectiveGrants } = useContext(PolicyContext);
  const { handleBlur, handleChange, setFieldValue, values, touched, errors } =
    useFormikContext<CoveredPartyPayloadType>();

  const { user } = useAuth0();

  // Edit Driver Form will have an identifier value
  // Add Driver Form will have an undefined identifier
  const isEditFormType = Boolean(values.identifier);

  // enable if add driver form or if you have permissions
  const canEditDetails =
    !isEditFormType ||
    editDriverDetailsPermissionsPolicy(resourceEffectiveGrants);

  // display only if you have permissions, whether adding or editing
  const hasExcludeDriverPermission = excludeDriverPermissionsPolicy(
    resourceEffectiveGrants
  );

  // States that have Exclude Driver disabled
  const cantExcludeDriver = ['US-VA', 'US-OR', 'US-WI', 'US-KY', 'US-UT'];

  // Other conditions affecting ability to exclude this driver
  const canExcludeDriver =
    values.policyPartyRelationship !== 'named_insured' &&
    !cantExcludeDriver.includes(details.region);

  const canEditDriverStatus = editDriverStatusPolicy(user!.effectiveGrants);

  const renderDriverNewQuestions = () => {
    if (!env.driverNewQuestionsEnabled) return null;

    const questions: ReactNode[] = [];

    const disabledTooltip = (() => {
      if (values.info.relationToApplicant === 'applicant') {
        return 'The primary insured must be an active driver on the policy.';
      }
      return isEditFormType && !canEditDriverStatus
        ? 'Please contact our agent advocate team to modify a driver’s status.'
        : null;
    })();

    if (driverOptionsQuestionsConfig.askLivesInHousehold) {
      questions.push(
        <Column className={styles.tooltip} key={'askLivesInHousehold'}>
          <Tooltip tooltip={disabledTooltip}>
            <FormInput
              variant="radioButtonGroup"
              label="Lives in primary household"
              name="info.exposureDetails.livesWithPolicyholder"
              options={{ true: 'yes', false: 'no' }}
              value={values.info.exposureDetails.livesWithPolicyholder}
              onChange={(value: string) =>
                setFieldValue(
                  'info.exposureDetails.livesWithPolicyholder',
                  value
                )
              }
              disabled={!!disabledTooltip}
              data-testid="lives-in-household"
            />
          </Tooltip>
        </Column>
      );
    }

    if (driverOptionsQuestionsConfig.askHasAccessToVehicles) {
      questions.push(
        <Column className={styles.tooltip} key={'askHasAccessToVehicles'}>
          <Tooltip tooltip={disabledTooltip}>
            <FormInput
              variant="radioButtonGroup"
              label="Regularly drives, or has regular access to any of the insured vehicles"
              name="info.exposureDetails.hasAccessToVehicles"
              options={{ true: 'yes', false: 'no' }}
              value={values.info.exposureDetails.hasAccessToVehicles}
              onChange={(value: string) =>
                setFieldValue('info.exposureDetails.hasAccessToVehicles', value)
              }
              disabled={!!disabledTooltip}
              data-testid="has-access-to-vehicles"
            />
          </Tooltip>
        </Column>
      );
    }

    if (driverOptionsQuestionsConfig.askHasOwnInsurancePolicy) {
      questions.push(
        <Column className={styles.tooltip} key={'askHasOwnInsurancePolicy'}>
          <Tooltip tooltip={disabledTooltip}>
            <FormInput
              variant="radioButtonGroup"
              label="Has their own insurance policy with Clearcover or another carrier"
              name="info.exposureDetails.hasOwnAutoPolicy"
              options={{ true: 'yes', false: 'no' }}
              value={values.info.exposureDetails.hasOwnAutoPolicy}
              onChange={(value: string) =>
                setFieldValue('info.exposureDetails.hasOwnAutoPolicy', value)
              }
              disabled={!!disabledTooltip}
              data-testid="has-own-insurance-policy"
            />
          </Tooltip>
        </Column>
      );
    }

    if (questions.length === 0) {
      return null;
    }

    while (questions.length < 3) {
      questions.push(<Column />);
    }

    return <Row>{questions}</Row>;
  };

  switch (values.policyPartyRelationship) {
    case 'excluded_person':
      return (
        <div className={styles.section} data-testid="driver-modal-info">
          <h1>Driver Info</h1>

          {/* Currently the UI doesn't support editing excluded drivers, but if that changes, this will cover the permissions */}
          {hasExcludeDriverPermission && (
            <Row>
              <Checkbox
                name="policyPartyRelationship"
                value={values.policyPartyRelationship === 'excluded_person'}
                label="Exclude driver from policy"
                onChange={() =>
                  setFieldValue('policyPartyRelationship', 'covered_person')
                }
              />
            </Row>
          )}

          <Row>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="text"
                name="info.firstName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.firstName}
                errorMessage={
                  touched.info?.firstName ? errors.info?.firstName : undefined
                }
                label="First Name"
                data-testid="first-name"
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="text"
                name="info.middleInitial"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.middleInitial}
                errorMessage={
                  touched.info?.middleInitial
                    ? errors.info?.middleInitial
                    : undefined
                }
                label="Middle Initial"
                data-testid="middle-initial"
              />
            </Column>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="text"
                name="info.lastName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.lastName}
                errorMessage={
                  touched.info?.lastName ? errors.info?.lastName : undefined
                }
                label="Last Name"
                data-testid="last-name"
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="selectAutocomplete"
                name="info.suffix"
                data-testid="suffix"
                onChange={(value: string) =>
                  setFieldValue('info.suffix', value)
                }
                onBlur={handleBlur}
                value={values.info.suffix}
                label="Suffix"
                options={{
                  jr: 'Jr',
                  sr: 'Sr',
                  i: 'I',
                  ii: 'II',
                  iii: 'III',
                  iv: 'IV',
                }}
              />
            </Column>
          </Row>
          <Row>
            <Column
              variant="quarter"
              className={classNames('fs-exclude', styles.driverInfoCol)}
            >
              <FormInput
                variant="text"
                name="info.dateOfBirth"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    'info.dateOfBirth',
                    monthDateYearNormalizer(e.currentTarget.value)
                  );
                }}
                onBlur={handleBlur}
                placeholder="MM/DD/YYYY"
                value={values.info.dateOfBirth}
                errorMessage={
                  touched.info?.dateOfBirth
                    ? errors.info?.dateOfBirth
                    : undefined
                }
                label="Date of Birth"
                data-testid="date-of-birth"
              />
            </Column>
            {excludedDriverOptionsQuestionsConfig?.askForGender && (
              <Column
                className={classNames('fs-exclude', styles.driverInfoCol)}
              >
                <FormInput
                  variant="selectAutocomplete"
                  name="info.gender"
                  data-testid="gender"
                  onChange={(value: string) =>
                    setFieldValue('info.gender', value)
                  }
                  onBlur={handleBlur}
                  value={values.info.gender}
                  errorMessage={
                    touched.info?.gender ? errors.info?.gender : undefined
                  }
                  label="Gender"
                  options={options.gender}
                  disabled={!canEditDetails}
                />
              </Column>
            )}
            {excludedDriverOptionsQuestionsConfig?.askForMaritalStatus && (
              <Column
                className={classNames('fs-exclude', styles.driverInfoCol)}
              >
                <FormInput
                  variant="selectAutocomplete"
                  name="info.maritalStatus"
                  data-testid="marital-status"
                  onChange={(value: string) =>
                    setFieldValue('info.maritalStatus', value)
                  }
                  onBlur={handleBlur}
                  value={values.info.maritalStatus}
                  errorMessage={
                    touched.info?.maritalStatus
                      ? errors.info?.maritalStatus
                      : undefined
                  }
                  label="Marital Status"
                  options={options.maritalStatus}
                  disabled={!canEditDetails}
                />
              </Column>
            )}
            {values.info.relationToApplicant.toLowerCase() !== 'applicant' && (
              <Column variant="quarter" className={styles.driverInfoCol}>
                <FormInput
                  variant="selectAutocomplete"
                  name="info.relationToApplicant"
                  data-testid="relation-to-applicant"
                  onChange={(value: string) =>
                    setFieldValue('info.relationToApplicant', value)
                  }
                  errorMessage={
                    touched.info?.relationToApplicant
                      ? errors.info?.relationToApplicant
                      : undefined
                  }
                  onBlur={handleBlur}
                  value={values.info.relationToApplicant}
                  label="Relation to Applicant"
                  options={options.relationToApplicant}
                />
              </Column>
            )}
          </Row>
        </div>
      );
    default:
      return (
        <div className={styles.section} data-testid="driver-modal-info">
          <h1>Driver Info</h1>
          {hasExcludeDriverPermission && canExcludeDriver ? (
            <Row>
              <Checkbox
                name="policyPartyRelationship"
                value={values.policyPartyRelationship === 'excluded_person'}
                label="Exclude driver from policy"
                onChange={() =>
                  setFieldValue('policyPartyRelationship', 'excluded_person')
                }
              />
            </Row>
          ) : null}
          <Row>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="text"
                name="info.firstName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.firstName}
                errorMessage={
                  touched.info?.firstName ? errors.info?.firstName : undefined
                }
                label="First Name"
                data-testid="first-name"
                disabled={!canEditDetails}
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="text"
                name="info.middleInitial"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.middleInitial}
                errorMessage={
                  touched.info?.middleInitial
                    ? errors.info?.middleInitial
                    : undefined
                }
                label="Middle Initial"
                data-testid="middle-initial"
                disabled={!canEditDetails}
              />
            </Column>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="text"
                name="info.lastName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.info.lastName}
                errorMessage={
                  touched.info?.lastName ? errors.info?.lastName : undefined
                }
                label="Last Name"
                data-testid="last-name"
                disabled={!canEditDetails}
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="selectAutocomplete"
                name="info.suffix"
                data-testid="suffix"
                onChange={(value: string) =>
                  setFieldValue('info.suffix', value)
                }
                onBlur={handleBlur}
                value={values.info.suffix}
                label="Suffix"
                options={{
                  jr: 'Jr',
                  sr: 'Sr',
                  i: 'I',
                  ii: 'II',
                  iii: 'III',
                  iv: 'IV',
                }}
                disabled={!canEditDetails}
              />
            </Column>
          </Row>
          <Row>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="text"
                name="info.dateOfBirth"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    'info.dateOfBirth',
                    monthDateYearNormalizer(e.currentTarget.value)
                  );
                }}
                onBlur={handleBlur}
                placeholder="MM/DD/YYYY"
                value={values.info.dateOfBirth}
                errorMessage={
                  touched.info?.dateOfBirth
                    ? errors.info?.dateOfBirth
                    : undefined
                }
                label="Date of Birth"
                data-testid="date-of-birth"
                disabled={!canEditDetails}
              />
            </Column>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="selectAutocomplete"
                name="info.gender"
                data-testid="gender"
                onChange={(value: string) =>
                  setFieldValue('info.gender', value)
                }
                onBlur={handleBlur}
                value={values.info.gender}
                errorMessage={
                  touched.info?.gender ? errors.info?.gender : undefined
                }
                label="Gender"
                options={options.gender}
                disabled={!canEditDetails}
              />
            </Column>
            <Column className={classNames('fs-exclude', styles.driverInfoCol)}>
              <FormInput
                variant="selectAutocomplete"
                name="info.maritalStatus"
                data-testid="marital-status"
                onChange={(value: string) =>
                  setFieldValue('info.maritalStatus', value)
                }
                onBlur={handleBlur}
                value={values.info.maritalStatus}
                errorMessage={
                  touched.info?.maritalStatus
                    ? errors.info?.maritalStatus
                    : undefined
                }
                label="Marital Status"
                options={options.maritalStatus}
                disabled={!canEditDetails}
              />
            </Column>
            {values.info.relationToApplicant.toLowerCase() !== 'applicant' && (
              <Column className={styles.driverInfoCol}>
                <FormInput
                  variant="selectAutocomplete"
                  name="info.relationToApplicant"
                  data-testid="relation-to-applicant"
                  onChange={(value: string) =>
                    setFieldValue('info.relationToApplicant', value)
                  }
                  errorMessage={
                    touched.info?.relationToApplicant
                      ? errors.info?.relationToApplicant
                      : undefined
                  }
                  onBlur={handleBlur}
                  value={values.info.relationToApplicant}
                  label="Relation to Applicant"
                  options={options.relationToApplicant}
                  disabled={!canEditDetails}
                />
              </Column>
            )}
          </Row>
          {renderDriverNewQuestions()}
        </div>
      );
  }
};
